import { useLayoutEffect, type RefObject } from 'react';
import { BREAKPOINT } from '@publishing/helpers';

const CARD_SELECTOR = '[data-card-slide-flex]';
const CARD_HEIGHT_PROPERTY = '--card-slide-flex-mobile-height';

export function useEqualMobileCardHeight(
  sectionRef: RefObject<HTMLElement | null>,
) {
  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const mobileMedia = window.matchMedia(`(max-width: ${BREAKPOINT}px)`);
    let frameId: number | undefined;

    const getCards = () =>
      Array.from(section.querySelectorAll<HTMLElement>(CARD_SELECTOR));

    const clearCardHeights = () => {
      getCards().forEach((card) => {
        card.style.removeProperty(CARD_HEIGHT_PROPERTY);
      });
    };

    const updateCardHeights = () => {
      const cards = getCards();
      clearCardHeights();
      if (!mobileMedia.matches) return;

      const cardGroups = new Map<HTMLElement, HTMLElement[]>();
      cards.forEach((card) => {
        const group = card.closest<HTMLElement>('[role="tabpanel"]')?.parentElement;
        if (!group) return;
        cardGroups.set(group, [...(cardGroups.get(group) ?? []), card]);
      });

      // hidden 패널은 높이가 0이므로 텍스트 영역만 복제해 실제 높이를 잽니다.
      const measureArea = document.createElement('div');
      Object.assign(measureArea.style, {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
      });
      section.append(measureArea);

      cardGroups.forEach((groupCards) => {
        const visibleCard = groupCards.find(
          (card) => card.getBoundingClientRect().width > 0,
        );
        const cardWidth = visibleCard?.getBoundingClientRect().width ?? 0;
        if (!cardWidth) return;

        const imageHeight = visibleCard?.lastElementChild?.getBoundingClientRect().height ?? 0;
        measureArea.style.width = `${cardWidth}px`;
        const maxHeight = groupCards.reduce((height, card) => {
          const content = card.firstElementChild?.cloneNode(true) as HTMLElement;
          if (!content) return height;
          measureArea.replaceChildren(content);
          return Math.max(
            height,
            content.getBoundingClientRect().height + imageHeight,
          );
        }, 0);
        if (!maxHeight) return;

        groupCards.forEach((card) => {
          card.style.setProperty(CARD_HEIGHT_PROPERTY, `${Math.ceil(maxHeight)}px`);
        });
      });
      measureArea.remove();
    };

    const scheduleUpdate = () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updateCardHeights);
    };

    updateCardHeights();
    window.addEventListener('resize', scheduleUpdate);
    mobileMedia.addEventListener('change', scheduleUpdate);
    document.fonts.addEventListener('loadingdone', scheduleUpdate);

    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId);
      window.removeEventListener('resize', scheduleUpdate);
      mobileMedia.removeEventListener('change', scheduleUpdate);
      document.fonts.removeEventListener('loadingdone', scheduleUpdate);
      clearCardHeights();
    };
  }, [sectionRef]);
}
